/**
 * Wall-clock (floating) date+times — a `YYYY-MM-DD HH:MM:SS` string with **no
 * timezone**: what a clock reads at the location, e.g. a manual flight's
 * `departureTimeStr` ("departs 14:30 at the origin"). It is **never shifted** for
 * the viewer and never used to compute a duration (there is no zone to subtract).
 * HTML `datetime-local` inputs carry the same instant as `YYYY-MM-DDTHH:MM`.
 *
 * Pure: string in, string out. Display is rendered through UTC purely so the
 * shown numbers are exactly the stored numbers, in every timezone.
 *
 * This tier is **flight-only** — manual flight leg times are the only wall-clock
 * strings the app displays (accommodations are real instants; see `instant.ts`).
 * So its display pins the 24-hour aviation clock unconditionally (issue #164)
 * rather than following the device locale.
 */

/** datetime-local input `YYYY-MM-DDTHH:MM` → wall-clock `YYYY-MM-DD HH:MM:SS`. `''` for empty. */
export const inputToWallClock = (value: string): string => {
	if (!value) return ''
	const [datePart, timePart = '00:00'] = value.split('T')
	const [h, mi] = timePart.split(':')
	return `${datePart} ${(h || '00').padStart(2, '0')}:${(mi || '00').padStart(2, '0')}:00`
}

/** Wall-clock `YYYY-MM-DD HH:MM:SS` → datetime-local `YYYY-MM-DDTHH:MM`. `''` for empty/invalid. */
export const wallClockToInput = (str: string | null | undefined): string => {
	if (!str) return ''
	const match = str.match(/^(\d{4}-\d{2}-\d{2})( (\d{2}):(\d{2}))?/)
	if (!match) return ''
	const [, datePart, , h, mi] = match
	if (!h) return `${datePart}T00:00`
	return `${datePart}T${h}:${mi}`
}

/**
 * Manual flight wall-clock string → display: `Jan 15, 14:30` (with time) or
 * `Jan 15` (date only). Aviation convention pins the 24-hour clock in every
 * locale (issue #164), so this ignores the device locale by design — it is the
 * deliberate exception to issue #165. Returns the input unchanged if it isn't a
 * wall-clock string, and `null` for empty input. Rendered in UTC so the
 * floating time is never shifted.
 */
export const formatFlightWallClock = (str: string | null | undefined): string | null => {
	if (!str) return null
	const match = str.match(/^(\d{4})-(\d{2})-(\d{2})( (\d{2}):(\d{2}):(\d{2}))?$/)
	if (!match) return str
	const [, y, mo, d, , h, mi] = match
	const date = new Date(
		Date.UTC(Number(y), Number(mo) - 1, Number(d), h ? Number(h) : 0, mi ? Number(mi) : 0),
	)
	if (h) {
		return date.toLocaleString('en-US', {
			month: 'short',
			day: 'numeric',
			minute: '2-digit',
			timeZone: 'UTC',
			// hourCycle (not hour12: false) so midnight is "00:30", never "24:30".
			hour: '2-digit',
			hourCycle: 'h23',
		})
	}
	return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/** The `YYYY-MM-DD` day of a wall-clock string or `datetime-local` value. `''` for empty/invalid. */
export const wallClockDay = (str: string | null | undefined): string => str?.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? ''
